import * as React from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { Select } from 'components/select/select';
import { Input } from 'components/input/input';
import { Button, buttonType } from 'components/button/button';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import './column-search.scss';

export interface ColumnSearchOption {
    id: number | string;
    title: string;
}

export interface ColumnSearchField {
    key: string;
    placeholder: string;
    dir?: 'ltr' | 'rtl';
    htmlType?: string;
    type?: 'text' | 'select' | 'date';
    options?: ColumnSearchOption[];
    // id/مقدارِ خنثی («همه») برای فیلدهای select؛ پیش‌فرض -1
    neutralId?: number | string;
}

interface Props {
    title: string;
    fields: ColumnSearchField[];
    values: Record<string, any>;
    onApply: (patch: Record<string, any>) => void;
}

// یک ذره‌بین کوچک کنار سرستون؛ با کلیک یک پاپ‌آور با اینپوت یا دراپ‌داون همان‌جا باز می‌شود.
// draft محلی نگه داشته می‌شود تا تایپ/انتخاب باعث رِرندر والد نشود؛ فقط با «جستجو» commit می‌شود.
export const ColumnSearch: React.FC<Props> = ({ title, fields, values, onApply }) => {
    const [open, setOpen] = React.useState<boolean>(false);
    const [draft, setDraft] = React.useState<Record<string, any>>({});
    // تقویم با portal بیرون از پاپ‌آور رندر می‌شود؛ تا وقتی باز است کلیک روی آن نباید پاپ‌آور را ببندد.
    const calendarOpenRef = React.useRef<boolean>(false);

    const handleOpenChange = (next: boolean) => {
        if (!next && calendarOpenRef.current) return;
        setOpen(next);
    };

    React.useEffect(() => {
        if (open) {
            const init: Record<string, any> = {};
            fields.forEach((f) => (init[f.key] = values[f.key]));
            setDraft(init);
        }
    }, [open]);

    const neutralOf = (f: ColumnSearchField) => f.neutralId ?? -1;

    const isActive = fields.some((f) => {
        const v = values[f.key];
        if (f.type === 'select') return v != null && v.id !== neutralOf(f);
        return (v ?? '') !== '';
    });

    const apply = () => {
        calendarOpenRef.current = false;
        onApply(draft);
        setOpen(false);
    };

    const clear = () => {
        const cleared: Record<string, any> = {};
        fields.forEach((f) => {
            if (f.type === 'select') cleared[f.key] = f.options?.find((o) => o.id === neutralOf(f)) ?? f.options?.[0];
            else cleared[f.key] = '';
        });
        calendarOpenRef.current = false;
        onApply(cleared);
        setOpen(false);
    };

    const content = (
        <div className="pmx-column-search-content" onClick={(e) => e.stopPropagation()}>
            {fields.map((f) =>
                f.type === 'select' ? (
                    <Select
                        key={f.key}
                        className="pmx-column-search-select"
                        value={draft[f.key]?.id}
                        placeholder={f.placeholder}
                        popupClassName="pmx-column-search-select-dropdown"
                        onChange={(value) =>
                            setDraft((d) => ({ ...d, [f.key]: f.options?.find((o) => o.id === value) }))
                        }
                        options={(f.options ?? []).map((o) => ({ value: o.id, label: o.title }))}
                    />
                ) : f.type === 'date' ? (
                    <DatePicker
                        key={f.key}
                        value={draft[f.key] || ''}
                        calendar={persian}
                        locale={persian_fa}
                        portal
                        // بالاتر از z-index پاپ‌آورِ antd (۱۰۳۰) تا تقویم زیر آن نرود
                        zIndex={1100}
                        onOpen={() => {
                            calendarOpenRef.current = true;
                        }}
                        onClose={() => {
                            calendarOpenRef.current = false;
                        }}
                        render={
                            <div>
                                <Input value={draft[f.key] || ''} icon={<CalendarIcon />} placeholder={f.placeholder} />
                            </div>
                        }
                        onChange={(e: any) => setDraft((d) => ({ ...d, [f.key]: e ? e.format() : '' }))}
                    />
                ) : (
                    <Input
                        key={f.key}
                        value={draft[f.key] ?? ''}
                        placeholder={f.placeholder}
                        dir={f.dir}
                        htmlType={f.htmlType}
                        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                        onPressEnter={apply}
                    />
                ),
            )}
            <div className="actions">
                <Button text="جستجو" type={buttonType.success} onClick={apply} />
                <Button text="حذف" type={buttonType.error} onClick={clear} />
            </div>
        </div>
    );

    return (
        <div className="pmx-column-search" onClick={(e) => e.stopPropagation()}>
            <span className="title">{title}</span>
            <Popover
                trigger="click"
                open={open}
                onOpenChange={handleOpenChange}
                content={content}
                placement="bottom"
                overlayClassName="pmx-column-search-overlay"
            >
                <SearchOutlined className={`search-icon ${isActive ? 'active' : ''}`} />
            </Popover>
        </div>
    );
};
